import { useRef, useState } from "react"
import type {
  PortalClientFile,
  PortalRequestResponse,
} from "@ledgerbox/domain"

import { errorMessage } from "./api"
import { usePortalApi } from "./api-context"
import { HiddenFileInput, InlineError } from "./ui"

/* The two items a client finishes somewhere else and then reports: access
   to their books, and a link to another site (an agreement or a payment).
   The engagement workbench and the plain checklist both draw them from
   here, so a client sees the same step either way. */

type Target = { requestId: string; itemId: string }

export function AccountingAction({
  request,
  item,
  files,
  firmName,
  uploadAllowed,
  onFiles,
  onRefresh,
}: {
  request: PortalRequestResponse
  item: PortalRequestResponse["items"][number]
  files: PortalClientFile[]
  firmName: string
  uploadAllowed: boolean
  onFiles(files: FileList | File[], target?: Target): Promise<void>
  onRefresh(): Promise<void>
}) {
  const api = usePortalApi()
  const [provider, setProvider] = useState(item.actionResponse?.provider ?? "")
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [copyNote, setCopyNote] = useState<string | null>(null)
  const input = useRef<HTMLInputElement>(null)
  const copy = async (value: string, label: string) => {
    setCopyNote(null)
    try {
      await navigator.clipboard.writeText(value)
      setCopyNote(`${label} copied`)
    } catch {
      setCopyNote(
        `Could not copy ${label.toLowerCase()}. Select and copy it instead.`
      )
    }
  }
  const save = async (
    selected: "quickbooks" | "xero" | "other" | "none",
    reportedComplete: boolean
  ) => {
    setPending(true)
    setError(null)
    try {
      await api.respondEngagementAction(request.requestId, item.itemId, {
        expectedVersion: item.evidenceVersion ?? 1,
        provider: selected,
        reportedComplete,
      })
      setProvider(selected)
      await onRefresh()
    } catch (cause) {
      setError(
        errorMessage(
          cause instanceof Error
            ? cause
            : new Error("Something went wrong. Try again.")
        )
      )
    } finally {
      setPending(false)
    }
  }
  const selected = provider || item.actionResponse?.provider
  const config =
    item.actionConfig?.type === "accounting_access" ? item.actionConfig : null
  return (
    <div className="engagement-action">
      <p>How do you keep your books?</p>
      <div
        className="engagement-provider-list"
        role="radiogroup"
        aria-label="Accounting system"
      >
        {(
          [
            ["quickbooks", "QuickBooks"],
            ["xero", "Xero"],
            ["other", "Another system"],
            ["none", "No accounting system"],
          ] as const
        ).map(([value, label]) => (
          <button
            key={value}
            type="button"
            role="radio"
            className="choice-pill"
            aria-checked={selected === value}
            disabled={pending || Boolean(request.clientDoneAt)}
            onClick={() => void save(value, false)}
          >
            {label}
          </button>
        ))}
      </div>
      {selected === "quickbooks" || selected === "xero" ? (
        <div className="engagement-guidance">
          <p>
            In {selected === "quickbooks" ? "QuickBooks" : "Xero"}, invite{" "}
            {firmName} to access your books
          </p>
          <p className="panel-note">
            This item completes itself when {firmName} connects your company
          </p>
          <ol className="engagement-guidance-steps">
            {selected === "quickbooks" ? (
              <>
                <li>Open Settings, then Manage users</li>
                <li>Choose Accounting Firms or Accountants, then Invite firm</li>
                <li>Enter the firm's details below and send the invitation</li>
              </>
            ) : (
              <>
                <li>Open Settings, then Users</li>
                <li>
                  Choose Invite a user and select a role that lets your firm
                  work on your books
                </li>
                <li>Enter the firm's email below and send the invitation</li>
              </>
            )}
          </ol>
          {config?.accountantEmail ? (
            <div className="engagement-copy-row">
              <span>
                Invite{" "}
                <strong className="mono">{config.accountantEmail}</strong>
              </span>
              <button
                type="button"
                className="text-button"
                onClick={() => void copy(config.accountantEmail!, "Email")}
              >
                Copy email
              </button>
            </div>
          ) : null}
          {selected === "quickbooks" && config?.quickbooksFirmId ? (
            <div className="engagement-copy-row">
              <span>
                Firm ID:{" "}
                <strong className="mono">{config.quickbooksFirmId}</strong>
              </span>
              <button
                type="button"
                className="text-button"
                onClick={() => void copy(config.quickbooksFirmId!, "Firm ID")}
              >
                Copy firm ID
              </button>
            </div>
          ) : null}
          {copyNote ? (
            <p role="status" className="panel-note">
              {copyNote}
            </p>
          ) : null}
          {config?.instructions ? <p>{config.instructions}</p> : null}
          <a
            href={
              selected === "quickbooks"
                ? "https://quickbooks.intuit.com/learn-support/en-us/help-article/account-management/managing-accountant-users-quickbooks-online/L2AcdYvHw_US_en_US"
                : "https://central.xero.com/0/guide/a5B3m000000rnPSEAY/get-ready-to-use-xero"
            }
            target="_blank"
            rel="noopener noreferrer"
          >
            See {selected === "quickbooks" ? "QuickBooks" : "Xero"} instructions
          </a>
          <button
            type="button"
            className="ghost-pill"
            disabled={pending || Boolean(request.clientDoneAt)}
            onClick={() => void save(selected, true)}
          >
            I invited the firm
          </button>
          <p className="panel-note">
            {firmName} will verify access. Do not share passwords, security
            codes, or bank login details here.
          </p>
        </div>
      ) : null}
      {selected === "other" || selected === "none" ? (
        <div className="engagement-guidance">
          <p>
            Upload the statements {firmName} asked for. Include every relevant
            account and period.
          </p>
          <HiddenFileInput
            ref={input}
            multiple
            onFiles={(selectedFiles) =>
              void onFiles(selectedFiles, {
                requestId: request.requestId,
                itemId: item.itemId,
              })
            }
          />
          <button
            type="button"
            className="ghost-pill"
            disabled={!uploadAllowed || Boolean(request.clientDoneAt)}
            onClick={() => input.current?.click()}
          >
            Add statements
          </button>
          {files.length ? (
            <p>
              {files.length} {files.length === 1 ? "file" : "files"} sent.{" "}
              {firmName} will check the dates they cover.
            </p>
          ) : null}
        </div>
      ) : null}
      {error ? <InlineError message={error} /> : null}
    </div>
  )
}

export function ExternalHandoff({
  request,
  item,
  onRefresh,
}: {
  request: PortalRequestResponse
  item: PortalRequestResponse["items"][number]
  onRefresh(): Promise<void>
}) {
  const api = usePortalApi()
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const config =
    item.actionConfig?.type === "external_handoff" ? item.actionConfig : null
  const activeLink = config && !config.expired ? config : null
  const report = async () => {
    setPending(true)
    setError(null)
    try {
      await api.respondEngagementAction(request.requestId, item.itemId, {
        expectedVersion: item.evidenceVersion ?? 1,
        reportedComplete: true,
      })
      await onRefresh()
    } catch (cause) {
      setError(
        errorMessage(
          cause instanceof Error
            ? cause
            : new Error("Something went wrong. Try again.")
        )
      )
    } finally {
      setPending(false)
    }
  }
  return (
    <div className="engagement-action">
      {activeLink ? (
        <a
          className="ghost-pill"
          href={activeLink.url}
          target="_blank"
          rel="noopener noreferrer"
        >
          Open {activeLink.category === "payment" ? "payment" : "agreement"}{" "}
          link
        </a>
      ) : (
        <InlineError
          message={
            config?.expired
              ? "This link no longer works. Ask the firm for a new one."
              : "Ask the firm for the link"
          }
        />
      )}
      {activeLink ? (
        <>
          <p>
            Opening the link does not mark this task complete. Tell the firm
            after you finish there.
          </p>
          <button
            type="button"
            className="ghost-pill"
            disabled={pending || Boolean(request.clientDoneAt)}
            onClick={() => void report()}
          >
            {pending ? "Saving…" : "I finished this step"}
          </button>
        </>
      ) : null}
      {item.actionResponse?.reportedComplete ? (
        <p className="panel-note">The firm will verify this step</p>
      ) : null}
      {error ? <InlineError message={error} /> : null}
    </div>
  )
}
